import {useEffect, useRef} from "react";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  모바일 디바이스 키보드 대응 — 실제로 보이는 영역(visual viewport) 노출
 * ════════════════════════════════════════════════════════════════════════════════
 *  왜 필요한가
 *   모바일에서 입력창을 누르면 디바이스 키보드가 올라오는데,
 *   iOS 사파리는 이때 window.innerHeight 와 100vh 를 그대로 둔다.
 *   그래서 height:100vh 로 잡아둔 채팅화면은 아래쪽(= 입력창)이
 *   키보드 뒤로 숨어버린다. 안드로이드는 브라우저/기기마다 동작이 달라
 *   innerHeight 가 줄어들기도 하고 아니기도 해서 역시 믿을 수 없다.
 *
 *   visualViewport 는 '지금 눈에 보이는 영역' 을 정확히 알려주므로
 *   이 값을 CSS 변수로 심어두고 화면 높이로 쓰면 어느 기기에서든
 *   입력창이 키보드 바로 위에 붙는다.
 *
 *  심어두는 변수
 *   --app-vvh : 지금 보이는 높이 (키보드가 뜨면 그만큼 줄어든다)
 *   --app-vvt : 뷰포트가 위로 밀려난 양 (iOS 는 키보드가 뜰 때 0 이 아닐 수 있다)
 *
 *  사용 예
 *   height: var(--app-vvh, 100vh)
 *   top:    var(--app-vvt, 0px)
 *
 *  ※ 지원하지 않는 브라우저에서는 변수를 심지 않는다.
 *    CSS 폴백(100vh)이 그대로 쓰이므로 기존 동작과 동일하다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 이 훅을 동시에 쓰는 화면 수 — 마지막 화면이 닫힐 때만 변수를 걷어낸다 */
let vvRefCount = 0;

const applyVars = () => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    root.style.setProperty('--app-vvh', `${Math.round(vv.height)}px`);
    root.style.setProperty('--app-vvt', `${Math.round(vv.offsetTop)}px`);
};

const clearVars = () => {
    const root = document.documentElement;
    root.style.removeProperty('--app-vvh');
    root.style.removeProperty('--app-vvt');
};

/**
 * @param active false 면 아무것도 하지 않는다 (탭이 닫혀있을 때 등)
 * @param onResize 뷰포트가 바뀔 때마다 호출 (스크롤 위치 보정용)
 */
export function useVisualViewportVars(active, onResize) {
    // onResize 는 인라인 함수로 넘어오는 경우가 많다.
    // 의존성에 넣으면 렌더마다 리스너를 붙였다 떼므로 ref 로 최신값만 참조한다.
    const onResizeRef = useRef(onResize);
    useEffect(() => {
        onResizeRef.current = onResize;
    }, [onResize]);

    useEffect(() => {
        const vv = window.visualViewport;
        if (!active || !vv) return;

        vvRefCount += 1;
        applyVars();

        const handleViewportChange = () => {
            applyVars();
            if (onResizeRef.current) onResizeRef.current();
        };

        vv.addEventListener('resize', handleViewportChange);
        vv.addEventListener('scroll', handleViewportChange);

        return () => {
            vv.removeEventListener('resize', handleViewportChange);
            vv.removeEventListener('scroll', handleViewportChange);
            vvRefCount -= 1;
            // 다른 화면이 아직 쓰고 있으면 변수를 남겨둔다
            if (vvRefCount <= 0) {
                vvRefCount = 0;
                clearVars();
            }
        };
    }, [active]);
}

export default useVisualViewportVars;
